import * as React from "react";
import { useState, useCallback, useRef, createContext, useContext } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "./cn";
import { Icon } from "./Icon";

/**
 * Context: true while the panel is playing its close animation.
 * Content and overlay use this to apply exit animation classes.
 */
const SidePanelClosingContext = createContext(false);

interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

/** Duration of the content slide-out animation */
const CLOSE_DURATION_MS = 200;

/**
 * SidePanel root component.
 * Controls the open/closed state of the side panel.
 *
 * Close animation strategy: intercept close requests, play the animation
 * while Radix stays open, then actually close after the animation finishes.
 */
function SidePanel({ open, onOpenChange, children }: SidePanelProps) {
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        // Opening — pass through immediately
        clearTimeout(closeTimerRef.current);
        setIsClosing(false);
        onOpenChange(true);
      } else if (!isClosing) {
        // Closing — start animation, delay actual close
        setIsClosing(true);
        closeTimerRef.current = setTimeout(() => {
          setIsClosing(false);
          onOpenChange(false);
        }, CLOSE_DURATION_MS);
      }
    },
    [isClosing, onOpenChange]
  );

  return (
    <SidePanelClosingContext.Provider value={isClosing}>
      <DialogPrimitive.Root open={open || isClosing} onOpenChange={handleOpenChange} modal>
        {children}
      </DialogPrimitive.Root>
    </SidePanelClosingContext.Provider>
  );
}
SidePanel.displayName = "SidePanel";

/**
 * SidePanel trigger component.
 * The element that opens the side panel when clicked.
 */
const SidePanelTrigger = DialogPrimitive.Trigger;

/**
 * SidePanel close component.
 * Closes the side panel when clicked.
 */
const SidePanelClose = DialogPrimitive.Close;

/**
 * Internal overlay component.
 */
const SidePanelOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const isClosing = useContext(SidePanelClosingContext);
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "side-panel-overlay fixed inset-0 z-50 bg-black/[0.08]",
        isClosing && "side-panel-closing",
        className
      )}
      {...props}
    />
  );
});
SidePanelOverlay.displayName = "SidePanelOverlay";

interface SidePanelContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Whether to show the default close button in the header.
   * @default false - Header should be added separately with SidePanelHeader
   */
  showCloseButton?: boolean;
}

/**
 * SidePanel content component.
 * The main container for side panel content that slides in from the right.
 *
 * @example
 * <SidePanel open={open} onOpenChange={setOpen}>
 *   <SidePanelContent>
 *     <SidePanelHeader title="Panel Title" onClose={() => setOpen(false)} />
 *     <SidePanelBody>
 *       <p>Panel content goes here.</p>
 *     </SidePanelBody>
 *     <SidePanelFooter>
 *       <Button>Action</Button>
 *     </SidePanelFooter>
 *   </SidePanelContent>
 * </SidePanel>
 */
const SidePanelContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  SidePanelContentProps
>(({ className, children, ...props }, ref) => {
  const isClosing = useContext(SidePanelClosingContext);

  return (
    <DialogPrimitive.Portal>
      <SidePanelOverlay />
      <DialogPrimitive.Content
        ref={ref}
        aria-describedby={undefined}
        className={cn(
          "side-panel-content fixed right-0 top-0 z-50 h-full w-full max-w-[568px] p-2",
          isClosing && "side-panel-closing",
          className
        )}
        {...props}
      >
        {/* Inner panel with styling */}
        <div
          className={cn(
            "h-full rounded-[32px] border border-border-tertiary bg-background",
            "shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]",
            "overflow-hidden flex flex-col"
          )}
        >
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
SidePanelContent.displayName = "SidePanelContent";

interface SidePanelHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The title to display in the header.
   */
  title: string;
  /**
   * Callback when the close button is clicked.
   * Shows X icon and closes the dialog.
   */
  onClose?: () => void;
  /**
   * Callback when the back button is clicked.
   * Shows left arrow icon and does NOT close the dialog.
   * Takes precedence over onClose if both are provided.
   */
  onBack?: () => void;
  /**
   * Optional actions to display on the right side of the header.
   */
  actions?: React.ReactNode;
}

/**
 * SidePanel header component.
 * Contains the close/back button (left) and centered title.
 * Shows back arrow if onBack is provided, otherwise shows close X.
 */
const SidePanelHeader = React.forwardRef<HTMLDivElement, SidePanelHeaderProps>(
  ({ className, title, onClose, onBack, actions, ...props }, ref) => {
    const buttonClasses =
      "w-10 h-10 rounded-full bg-surface-1 flex items-center justify-center hover:bg-surface-2 transition-colors focus:outline-none";

    return (
      <div
        ref={ref}
        className={cn("flex-shrink-0 flex items-center px-5 py-6", className)}
        {...props}
      >
        {/* Back or Close button - 40px circle with surface-1 background */}
        {onBack ? (
          <button
            onClick={onBack}
            className={buttonClasses}
            aria-label="Go back"
          >
            <Icon name="arrow-left" size={17} className="text-text-secondary" />
          </button>
        ) : (
          <DialogPrimitive.Close asChild>
            <button
              className={buttonClasses}
              aria-label="Close panel"
            >
              <Icon name="xmark" size={17} className="text-text-secondary" />
            </button>
          </DialogPrimitive.Close>
        )}

        {/* Centered title - 18px GT Walsheim Medium */}
        <DialogPrimitive.Title className={cn("flex-1 text-center text-[18px] font-medium leading-none text-text-primary", actions ? "" : "pr-10")}>
          {title}
        </DialogPrimitive.Title>

        {/* Optional right-side actions */}
        {actions}
      </div>
    );
  }
);
SidePanelHeader.displayName = "SidePanelHeader";

/**
 * SidePanel body component.
 * Scrollable content area with proper padding and autohide scrollbars.
 */
const SidePanelBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex-1 overflow-y-auto px-5 py-6",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
SidePanelBody.displayName = "SidePanelBody";

/**
 * SidePanel footer component.
 * Optional footer for actions, fixed at the bottom.
 */
const SidePanelFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex-shrink-0 px-5 py-6 border-t border-border-tertiary",
      className
    )}
    {...props}
  />
));
SidePanelFooter.displayName = "SidePanelFooter";

export {
  SidePanel,
  SidePanelTrigger,
  SidePanelClose,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
  type SidePanelContentProps,
  type SidePanelHeaderProps,
};
